import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Lock } from "lucide-react";
import { ChangeReasonDialog, ChangeReasonPrompt } from "@/components/orders/ChangeReasonDialog";
import { CancelOrderDialog } from "@/components/orders/CancelOrderDialog";
import {
  ChangeEventType,
  EVENT_LABELS,
  REASON_LABELS,
  REASONS_BY_EVENT,
  SYSTEM_ONLY_REASONS,
  userSelectableReasons,
} from "@/lib/change-events";

interface Variant {
  id: string;
  name: string;
  description: string;
  events: ChangeReasonPrompt[];
  title?: string;
  confirmLabel?: string;
}

const SAMPLE_ORDER = "AOSC-AURY-00462";

const VARIANTS: Variant[] = [
  {
    id: "trip_created_manual",
    name: "Trip created (Manual)",
    description: "Shown when a dispatcher creates a manual trip.",
    events: [{ key: "created", eventType: "trip_created_manual" }],
  },
  {
    id: "trip_type_updated",
    name: "Trip type pivot",
    description: "Shown when trip_type changes between two non-empty values.",
    events: [
      {
        key: "trip_type",
        eventType: "trip_type_updated",
        fromLabel: "NVO",
        toLabel: "Manual",
      },
    ],
  },
  {
    id: "trip_equipment_trailer",
    name: "Trailer pivot",
    description: "Shown when the assigned trailer is swapped.",
    events: [
      {
        key: "trailer_id",
        eventType: "trip_equipment_trailer",
        fromLabel: "H30303",
        toLabel: "H30411",
      },
    ],
  },
  {
    id: "trip_equipment_tractor",
    name: "Tractor pivot",
    description: "Shown when the assigned tractor is swapped.",
    events: [
      {
        key: "assigned_tractor_id",
        eventType: "trip_equipment_tractor",
        fromLabel: "T-1042",
        toLabel: "T-2287",
      },
    ],
  },
  {
    id: "trip_equipment_tractor_unassigned",
    name: "Tractor pivot (previously unassigned)",
    description:
      "Single-event pivot where there was no prior assignment — shows an em dash on the original side.",
    events: [
      {
        key: "assigned_tractor_id",
        eventType: "trip_equipment_tractor",
        fromLabel: null,
        toLabel: "T-2287",
      },
    ],
  },
  {
    id: "multi",
    name: "Multi-event (order edit)",
    description: "One submit that changes trip type, trailer and tractor at once.",
    events: [
      {
        key: "trip_type",
        eventType: "trip_type_updated",
        detail: "NVO → Manual",
        fromLabel: "NVO",
        toLabel: "Manual",
      },
      {
        key: "trailer_id",
        eventType: "trip_equipment_trailer",
        detail: "H30303 → H30411",
        fromLabel: "H30303",
        toLabel: "H30411",
      },
      {
        key: "assigned_tractor_id",
        eventType: "trip_equipment_tractor",
        detail: "T-1042 → T-2287",
        fromLabel: "T-1042",
        toLabel: "T-2287",
      },
    ],
  },
];

const ALL_EVENTS = Object.keys(REASONS_BY_EVENT) as ChangeEventType[];
const ALL_REASONS = Array.from(
  new Set(ALL_EVENTS.flatMap((e) => REASONS_BY_EVENT[e])),
);

/**
 * Review-only gallery of every trip pivot / change-reason dialog variant.
 * Renders the real components with sample data — no writes are performed.
 */
export default function DevPivotDialogs() {
  const [openVariant, setOpenVariant] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const active = VARIANTS.find((v) => v.id === openVariant) ?? null;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">Trip pivot dialogs — preview</h1>
          <p className="text-sm text-muted-foreground">
            Every change-reason dialog variant rendered with sample data. Confirming
            here only logs the payload below; nothing is persisted.
          </p>
        </header>

        <div className="grid gap-4 sm:grid-cols-2">
          {VARIANTS.map((v) => (
            <Card key={v.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{v.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{v.description}</p>
                <div className="flex flex-wrap gap-1">
                  {v.events.map((e) => (
                    <Badge key={e.key} variant="secondary">
                      {EVENT_LABELS[e.eventType]}
                    </Badge>
                  ))}
                </div>
                <Button size="sm" onClick={() => setOpenVariant(v.id)}>
                  Open dialog
                </Button>
              </CardContent>
            </Card>
          ))}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Cancel trip</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                CancelOrderDialog — same dialog with a cancellation-specific title
                and confirm label.
              </p>
              <div className="flex flex-wrap gap-1">
                <Badge variant="secondary">{EVENT_LABELS.trip_cancelled}</Badge>
              </div>
              <Button size="sm" variant="destructive" onClick={() => setCancelOpen(true)}>
                Open dialog
              </Button>
            </CardContent>
          </Card>
        </div>

        {lastResult && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Last confirm payload</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">
                {lastResult}
              </pre>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Reason matrix</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-4 font-medium">Reason</th>
                  {ALL_EVENTS.map((e) => (
                    <th key={e} className="px-2 py-2 font-medium">
                      {EVENT_LABELS[e]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ALL_REASONS.map((r) => (
                  <tr key={r} className="border-b last:border-0">
                    <td className="py-2 pr-4">
                      {REASON_LABELS[r]}
                      {SYSTEM_ONLY_REASONS.has(r) && (
                        <Badge variant="outline" className="ml-2">
                          system only
                        </Badge>
                      )}
                    </td>
                    {ALL_EVENTS.map((e) => (
                      <td key={e} className="px-2 py-2 text-muted-foreground">
                        {REASONS_BY_EVENT[e].includes(r) ? (
                          userSelectableReasons(e).includes(r) ? (
                            <Check className="h-4 w-4 text-primary" aria-label="Selectable" />
                          ) : (
                            <Lock className="h-4 w-4" aria-label="System only" />
                          )
                        ) : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {active && (
        <ChangeReasonDialog
          open={!!openVariant}
          onOpenChange={(o) => !o && setOpenVariant(null)}
          events={active.events}
          orderUniqueId={SAMPLE_ORDER}
          title={active.title}
          confirmLabel={active.confirmLabel}
          onConfirm={(results) => {
            setLastResult(JSON.stringify(results, null, 2));
            setOpenVariant(null);
          }}
        />
      )}

      <CancelOrderDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        orderId={SAMPLE_ORDER}
        onConfirm={(payload) => {
          setLastResult(JSON.stringify(payload, null, 2));
          setCancelOpen(false);
        }}
      />
    </div>
  );
}